/**
 * HomeHero — condensed dashboard band (zone 1 of the SUPER AWENI home).
 *
 * Pattern: "complications row" (Apple Watch / iOS 17 Standby / Material 3
 * dashboard cards). Renders SYNCHRONOUSLY at mount with cached data only;
 * click each chip to open the full widget for deep details.
 *
 * Layout:
 *   [📍 City]                                          [↻]
 *   As-salaamu alaykum, {name}
 *
 *   ┌────────────────┐ ┌─────────┐ ┌──────────┐ ┌──────────────┐
 *   │ 📅 Wed May 6   │ │ 🕐 06:02│ │ 🌤 18°   │ │ 🕌 Sunrise   │
 *   │ 21 Dhū al-Q.   │ │ tz      │ │ feels 16°│ │ in 50 min    │
 *   └────────────────┘ └─────────┘ └──────────┘ └──────────────┘
 *      → CalendarModal   → TimeModal  → /weather   → /prayer
 *
 * Each chip is a single source of truth — no duplicate cards in the feed
 * below for date/time/weather/prayer. Tap = drill into deep details.
 *
 * MUST stay synchronous + side-effect-free (per CLAUDE.md "Home Page
 * Anti-Regression"): no fetch, no geolocation prompt, no heavy compute.
 * Time tick is a single per-minute interval, cleaned up on unmount.
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import type { LocationService } from '../../services/LocationService';
import type { WeatherService } from '../../services/WeatherService';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
import { CalendarModal } from './CalendarModal';
import { TimeModal } from './TimeModal';
import { LocationHeader } from '../../shell/LocationHeader';
import { swr } from '../../services/swr';

interface TrendPayload {
  ok?: boolean;
  data?: {
    user_id?: number;
    week_start?: string;
    generated_at?: number;
    highlights?: string[];
    dimensions?: Record<string, unknown>;
  };
  // Some shapes return the data at top level (depending on ResponseFormatter)
  highlights?: string[];
}

async function fetchTrendRadar(): Promise<TrendPayload | null> {
  try {
    const r = await fetch('/aweni/api/v1/trend-radar', {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!r.ok) return null;
    const data = (await r.json()) as TrendPayload;
    return data;
  } catch {
    return null;
  }
}

export interface HomeHeroDeps {
  ctx: AweniContext;
  locationService: LocationService;
  weatherService: WeatherService;
  onChooseCity: () => void;
  onTapPrayer: () => void;
  onTapSettings: () => void;
}

export class HomeHero {
  private root: HTMLElement | null = null;
  private deps: HomeHeroDeps;
  private timeTick: ReturnType<typeof setInterval> | null = null;
  private locationHeader: LocationHeader | null = null;
  /** T3-14 — interval that rotates trend-radar highlights every 6s. */
  private trendRotator: ReturnType<typeof setInterval> | null = null;
  /** Unsubscribe handle for prayerTimeService.onChange — fixes the
   *  "Dhuhr at 13:27 vs strip 13:32" mismatch where the hero rendered
   *  once with stale data and never updated when the server response
   *  refined the calculation. */
  private prayerUnsub: (() => void) | null = null;

  constructor(deps: HomeHeroDeps) {
    this.deps = deps;
  }

  /** Render synchronously into the given parent element. */
  mount(parent: HTMLElement): void {
    const root = document.createElement('section');
    root.className = 'aweni-hero';
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('home_hero_label') || 'Your daily summary');
    this.root = root;
    parent.appendChild(root);
    this.render();
    this.startTimeTick();

    // Re-render the hero whenever the prayer service updates (server
    // response lands, method/madhab changes, force-local toggle, etc.)
    // Without this, the chip can show a stale prayer time while the
    // strip below shows the refined value — the bug surfaced as
    // "Dhuhr at 13:27" in the chip vs "DHUHR 13:32" in the strip.
    this.prayerUnsub = prayerTimeService.onChange(() => {
      this.render();
    });

    // Mount the unified LocationHeader into the dedicated slot. It owns all
    // location interaction (auto-detect + favorites + search) and notifies
    // every consuming widget through LocationService.onChange().
    const slot = root.querySelector<HTMLElement>('[data-loc-header-slot]');
    if (slot) {
      this.locationHeader = new LocationHeader({
        ctx: this.deps.ctx,
        collapsible: true,
        onChange: () => {
          // Re-render hero so chips reflect the new city/weather/prayer
          // immediately. Widgets listen to LocationService.onChange()
          // independently — this just keeps the hero in sync.
          this.render();
        },
      });
      this.locationHeader.mount(slot);
    }
  }

  /** Re-render the hero (called when location/weather/prayer data changes). */
  render(): void {
    if (!this.root) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';
    const userName = this.deps.ctx.auth.user?.name?.split(' ')[0] || '';
    const isGuest = !!this.deps.ctx.auth.user?.is_guest;
    const locale = this.deps.ctx.i18n.locale;

    const loc = this.deps.locationService.getState();
    const weather = this.deps.weatherService.getData();
    const hasLocation = loc.lat !== null && loc.lng !== null;

    const greeting = isGuest
      ? t('home_hero_greeting_guest')
      : (userName ? t('home_hero_greeting_named', { name: userName }) : t('home_hero_greeting_anon'));

    // Compute chip data.
    const date = this._dateChipData(locale);
    const time = this._timeChipData(locale);
    const wx = this._weatherChipData(weather, t);
    const pr = this._prayerChipData(hasLocation, t);

    // Preserve mounted LocationHeader between renders — only set its slot
    // markup once. The hero re-renders frequently (per-minute time tick,
    // weather refresh) but the header should persist its expanded state
    // and listeners across those re-renders.
    const existingSlot = this.root.querySelector<HTMLElement>('[data-loc-header-slot]');
    const preserveLocHeader = existingSlot && this.locationHeader;

    this.root.innerHTML = `
      <h1 class="aweni-hero-greeting" dir="${isRtl ? 'rtl' : 'ltr'}">${escHtml(greeting)}</h1>
      <div data-trend-highlight class="aweni-hero-trend" hidden></div>
      <div data-loc-header-slot class="aweni-hero-loc-slot"></div>

      <div class="aweni-hero-chips" role="group" aria-label="${t('home_hero_chips_aria') || 'Today summary'}">
        <button type="button" class="aweni-hero-chip" data-action="open-calendar"
                aria-label="${escAttr(date.aria)}">
          <span class="aweni-hero-chip-icon">📅</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary">${escHtml(date.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(date.secondary)}</span>
          </span>
        </button>

        <button type="button" class="aweni-hero-chip" data-action="open-time"
                aria-label="${escAttr(time.aria)}">
          <span class="aweni-hero-chip-icon">🕐</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary" data-tick="time">${escHtml(time.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(time.secondary)}</span>
          </span>
        </button>

        <a class="aweni-hero-chip" href="#/weather" data-action="open-weather"
           aria-label="${escAttr(wx.aria)}">
          <span class="aweni-hero-chip-icon">${wx.icon}</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary">${escHtml(wx.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(wx.secondary)}</span>
          </span>
        </a>

        <a class="aweni-hero-chip" href="#/prayer" data-action="open-prayer"
           aria-label="${escAttr(pr.aria)}">
          <span class="aweni-hero-chip-icon">🕌</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary">${escHtml(pr.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(pr.secondary)}</span>
          </span>
        </a>
      </div>
    `;

    // Re-attach the existing LocationHeader into the new slot if we had one.
    if (preserveLocHeader) {
      const newSlot = this.root.querySelector<HTMLElement>('[data-loc-header-slot]');
      // The header's own root is detached when the parent innerHTML is
      // overwritten; re-mount it into the new slot. Since LocationHeader
      // owns its DOM and listeners, we tear it down and re-create instead
      // of reaching into private state.
      this.locationHeader?.unmount();
      if (newSlot) {
        this.locationHeader = new LocationHeader({
          ctx: this.deps.ctx,
          collapsible: true,
          onChange: () => this.render(),
        });
        this.locationHeader.mount(newSlot);
      }
    }

    this._wireHandlers();
  }

  private _wireHandlers(): void {
    if (!this.root) return;
    const root = this.root;

    // Date chip → CalendarModal
    root.querySelector('[data-action="open-calendar"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      const loc = this.deps.locationService.getState();
      try {
        new CalendarModal({
          ctx: this.deps.ctx,
          defaultCountryCode: loc.countryCode || null,
        }).show();
      } catch (err) {
        console.warn('[HomeHero] CalendarModal failed:', err);
      }
    });

    // Time chip → TimeModal (overlay with TimeWidget expanded)
    root.querySelector('[data-action="open-time"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      try {
        new TimeModal(this.deps.ctx).show();
      } catch (err) {
        console.warn('[HomeHero] TimeModal failed:', err);
      }
    });

    // Weather chip → /weather (native <a href>, also trigger analytics hook later)
    root.querySelector('[data-action="open-weather"]')?.addEventListener('click', () => {
      // No preventDefault — let the hash route fire
    });

    // Prayer chip → /prayer (native <a href>) + analytics hook
    root.querySelector('[data-action="open-prayer"]')?.addEventListener('click', (e) => {
      this.deps.onTapPrayer();
      void e;
    });

    // T3-14 — TrendRadar highlights strip below the greeting. Renders
    // synchronously from cache (if any), then revalidates via SWR. Skipped
    // for guests (the endpoint requires JWT and the values are per-user).
    this._mountTrendHighlights();
  }

  /** T3-14 — render up to 3 weekly highlights, rotating every 6s. */
  private _mountTrendHighlights(): void {
    const root = this.root;
    if (!root) return;
    const slot = root.querySelector<HTMLElement>('[data-trend-highlight]');
    if (!slot) return;
    if (this.deps.ctx.auth.user?.is_guest) {
      slot.hidden = true;
      return;
    }

    const renderHighlights = (highlights: string[]): void => {
      if (!highlights.length) {
        slot.hidden = true;
        return;
      }
      slot.hidden = false;
      slot.innerHTML = `
        <span class="aweni-hero-trend-icon" aria-hidden="true">✨</span>
        <span class="aweni-hero-trend-text" data-trend-line>${escHtml(highlights[0])}</span>
      `;
      // Stop any prior rotator before starting a new one.
      if (this.trendRotator !== null) { clearInterval(this.trendRotator); this.trendRotator = null; }
      if (highlights.length > 1) {
        let idx = 0;
        this.trendRotator = setInterval(() => {
          idx = (idx + 1) % highlights.length;
          const line = root.querySelector<HTMLElement>('[data-trend-line]');
          if (line) {
            line.style.opacity = '0';
            setTimeout(() => {
              line.textContent = highlights[idx] || '';
              line.style.opacity = '1';
            }, 180);
          }
        }, 6000);
      }
    };

    void swr<TrendPayload | null>(
      'trend_radar_v1',
      () => fetchTrendRadar(),
      {
        freshTtlMs: 5 * 60 * 1000,
        staleTtlMs: 60 * 60 * 1000,
        onUpdate: (data) => {
          const highlights = data?.data?.highlights ?? data?.highlights ?? [];
          renderHighlights(highlights);
        },
      },
    ).then((data) => {
      const highlights = data?.data?.highlights ?? data?.highlights ?? [];
      renderHighlights(highlights);
    }).catch(() => { /* silent — feed simply doesn't render the strip */ });
  }

  /** Per-minute tick that updates only the time chip's primary text — no
   *  full re-render to avoid touching the rest of the hero. */
  private startTimeTick(): void {
    if (this.timeTick !== null) return;
    const update = (): void => {
      const el = this.root?.querySelector<HTMLElement>('[data-tick="time"]');
      if (!el) return;
      const locale = this.deps.ctx.i18n.locale;
      try {
        el.textContent = new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date());
      } catch {
        el.textContent = new Date().toLocaleTimeString(undefined, {
          hour: '2-digit', minute: '2-digit',
        });
      }
    };
    // Align with next minute boundary, then tick every 60s.
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    setTimeout(() => {
      update();
      this.timeTick = setInterval(update, 60_000);
    }, msToNextMinute);
  }

  unmount(): void {
    if (this.timeTick !== null) {
      clearInterval(this.timeTick);
      this.timeTick = null;
    }
    if (this.trendRotator !== null) {
      clearInterval(this.trendRotator);
      this.trendRotator = null;
    }
    if (this.prayerUnsub) {
      this.prayerUnsub();
      this.prayerUnsub = null;
    }
    this.locationHeader?.unmount();
    this.locationHeader = null;
    this.root?.remove();
    this.root = null;
  }

  // ---------- chip data computation ---------------------------------------

  private _dateChipData(locale: string): { primary: string; secondary: string; aria: string } {
    const now = new Date();
    let primary = '';
    try {
      primary = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(now);
    } catch {
      primary = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    const hijri = formatHijri(gregorianToHijri(now), locale);
    return {
      primary,
      secondary: hijri,
      aria: this.deps.ctx.i18n.t('home_hero_date_aria') || 'Open calendar',
    };
  }

  private _timeChipData(locale: string): { primary: string; secondary: string; aria: string } {
    const now = new Date();
    let primary = '';
    try {
      primary = new Intl.DateTimeFormat(locale, {
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(now);
    } catch {
      primary = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* */ }
    const tzShort = tz ? tz.split('/').pop() || tz : '';
    return {
      primary,
      secondary: tzShort,
      aria: this.deps.ctx.i18n.t('home_hero_time_aria') || 'Open time, world clock, stopwatch',
    };
  }

  private _weatherChipData(
    weather: ReturnType<WeatherService['getData']>,
    t: (k: string, p?: Record<string, string | number>) => string,
  ): { primary: string; secondary: string; icon: string; aria: string } {
    if (!weather || typeof weather.temperature !== 'number') {
      return {
        primary: t('home_hero_weather_none') || '—',
        secondary: t('weather_loading') || '',
        icon: '🌤',
        aria: t('home_hero_weather_aria') || 'Open weather',
      };
    }
    const temp = `${Math.round(weather.temperature)}°`;
    const feels = typeof weather.feelsLike === 'number'
      ? `${t('weather_feels_like') || 'feels'} ${Math.round(weather.feelsLike)}°`
      : '';
    return {
      primary: temp,
      secondary: feels,
      icon: this._weatherEmoji(weather),
      aria: t('home_hero_weather_aria') || 'Open weather',
    };
  }

  private _prayerChipData(
    hasLocation: boolean,
    t: (k: string, p?: Record<string, string | number>) => string,
  ): { primary: string; secondary: string; aria: string } {
    if (!hasLocation) {
      return {
        primary: t('home_hero_prayer_none') || '—',
        secondary: t('home_hero_choose_city') || 'Set city',
        aria: t('home_hero_prayer_aria') || 'Open prayer times',
      };
    }
    try {
      const next = prayerTimeService.getNextPrayer();
      if (next) {
        const prayerName = t(`prayer_${next.name}`) || next.name;
        const minsTotal = Math.max(0, Math.round(next.minutesUntil));
        const hours = Math.floor(minsTotal / 60);
        const mins = minsTotal % 60;
        // Format "Hh MMmin" — no space, two-digit minutes — prevents
        // misreading "2h 9m" as two separate values per user feedback.
        const compact = hours > 0
          ? t('home_hero_in_hm', { h: String(hours), m: String(mins).padStart(2, '0') })
          : t('home_hero_in_m', { m: String(mins) });
        // Format the prayer's clock time in 24h (matches the dense Prayer
        // page hero) so the user has the absolute time without tapping.
        let prayerTimeShort = '';
        try {
          prayerTimeShort = new Intl.DateTimeFormat(this.deps.ctx.i18n.locale, {
            hour: '2-digit', minute: '2-digit', hour12: false,
          }).format(next.time);
        } catch { /* fall back to empty string */ }
        const primary = prayerTimeShort
          ? (t('prayer_at_time', { prayer: prayerName, time: prayerTimeShort })
              || `${prayerName} at ${prayerTimeShort}`)
          : prayerName;
        const inPrefix = t('glance_strip_in_prefix') || 'in ';
        return {
          primary,
          secondary: `${inPrefix}${compact}`,
          aria: t('home_hero_prayer_aria') || 'Open prayer times',
        };
      }
    } catch { /* prayer service unavailable */ }
    return {
      primary: t('home_hero_prayer_none') || '—',
      secondary: '',
      aria: t('home_hero_prayer_aria') || 'Open prayer times',
    };
  }

  /** Map weather data to a single-emoji glyph. */
  private _weatherEmoji(weather: ReturnType<WeatherService['getData']>): string {
    if (!weather) return '🌤';
    if (weather.rain && weather.rain > 0) return '🌧️';
    if ((weather.precipitationProbability || 0) > 60) return '🌦️';
    if ((weather.humidity || 0) > 80) return '☁️';
    if (typeof weather.temperature === 'number' && weather.temperature > 30) return '☀️';
    if (typeof weather.temperature === 'number' && weather.temperature < 0) return '❄️';
    return '🌤️';
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
